import { LIST_VARIANTS } from '../constants';
import { useCurrentList } from './useCurrentList';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import {
  getBulletedListHotkey,
  getDecreaseIndentHotkey,
  getIncreaseIndentHotkey,
  getNumberedListHotkey,
} from '../utils';
import { toggleList } from '../toggleFunctions';
import { useSlateStatic } from 'domains/reporter/RichTextEditor/core';
import { useSendModifyDictationMessage } from 'common/Recorder/useRecorder';

export type ToolbarButtonConfig = {
  icon: React.ReactNode;
  label: string;
  secondaryLabel: string;
  onMouseDown: () => void;
  selected?: boolean;
};

export type UseListInteractionReturn = {
  toolbarButtonConfigs: Array<ToolbarButtonConfig>;
  toolbarDividerIndex: number;
};

export const useListInteraction = (): UseListInteractionReturn => {
  const { currentActiveListVariant, decreaseIndent, increaseIndent } = useCurrentList();

  const editor = useSlateStatic();
  const sendModifyDictationMessage = useSendModifyDictationMessage();

  // The order of this array represents the order it will show up on the toolbar.
  // The TOOLBAR_DIVIDER_INDEX represents the position at which the divider will slice the array
  const TOOLBAR_DIVIDER_INDEX = 2;
  const toolbarButtonConfigs: Array<ToolbarButtonConfig> = [
    {
      icon: <FormatListBulletedIcon />,
      label: 'Bulleted List',
      secondaryLabel: getBulletedListHotkey(true),
      onMouseDown: () => {
        sendModifyDictationMessage();
        toggleList(editor, currentActiveListVariant, LIST_VARIANTS.ul);
      },
      selected: currentActiveListVariant === LIST_VARIANTS.ul,
    },
    {
      icon: <FormatListNumberedIcon />,
      label: 'Numbered List',
      secondaryLabel: getNumberedListHotkey(true),
      onMouseDown: () => {
        sendModifyDictationMessage();
        toggleList(editor, currentActiveListVariant, LIST_VARIANTS.ol);
      },
      selected: currentActiveListVariant === LIST_VARIANTS.ol,
    },
    {
      icon: <FormatIndentDecreaseIcon />,
      label: 'Decrease Indent',
      secondaryLabel: getDecreaseIndentHotkey(true),
      onMouseDown: () => {
        sendModifyDictationMessage();
        decreaseIndent();
      },
    },
    {
      icon: <FormatIndentIncreaseIcon />,
      label: 'Increase Indent',
      secondaryLabel: getIncreaseIndentHotkey(true),
      onMouseDown: () => {
        sendModifyDictationMessage();
        increaseIndent();
      },
    },
  ];

  return {
    toolbarButtonConfigs,
    toolbarDividerIndex: TOOLBAR_DIVIDER_INDEX,
  };
};
